import { useEffect, useMemo, useRef, useState } from "react";
import api from "../lib/typedApi";
import { PageHeader, NBCard, NBInput, NBSelect, NBButton, EmptyState } from "../components/UI";
import { toast } from "sonner";
import { Plus, Trash2, X, TrendingUp, TrendingDown, DollarSign, ScanLine, Sparkles, Camera } from "lucide-react";

const CATEGORIES = ["food", "rent", "utilities", "transport", "education", "entertainment", "health", "savings", "salary", "other"];
// Receipt-scan categories (a superset; we map to existing CATEGORIES on confirm)
const SCAN_CATEGORY_MAP = {
  groceries: "food", dining: "food", fuel: "transport", utilities: "utilities",
  household: "other", kids: "education", pets: "other", health: "health",
  entertainment: "entertainment", transport: "transport", gifts: "other",
  clothing: "other", other: "other",
};

export default function Budget() {
  const [entries, setEntries] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ type: "expense", amount: "", category: "food", description: "", date: new Date().toISOString().slice(0, 10) });

  // Receipt scan
  const fileInputRef = useRef(null);
  const [scanning, setScanning] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [confirming, setConfirming] = useState(false);

  const load = async () => {
    const { data } = await api.get("/api/budget");
    setEntries(data);
  };
  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/api/budget", { ...form, amount: parseFloat(form.amount) });
      toast.success("Saved!");
      setOpen(false);
      setForm({ type: "expense", amount: "", category: "food", description: "", date: new Date().toISOString().slice(0, 10) });
      load();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => { await api.delete(`/api/budget/${id}`); load(); };

  const onReceiptPicked = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    setScanning(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/api/budget/scan-receipt", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Pre-fill the editable preview
      const items = (data.line_items || []).map((it) => ({
        type: "expense",
        amount: Number(it.amount) || 0,
        category: SCAN_CATEGORY_MAP[(it.category || "other").toLowerCase()] || "other",
        description: it.description || "",
        _id: Math.random().toString(36).slice(2),
      }));
      setScanResult({
        vendor: data.vendor || "",
        date: data.date || new Date().toISOString().slice(0, 10),
        total: data.total,
        currency: data.currency || "USD",
        confidence: data.confidence || "medium",
        items,
      });
      toast.success(`Parsed ${items.length} line item${items.length === 1 ? "" : "s"}`);
    } catch (err) {
      const detail = err.response?.data?.detail || "Scan failed";
      toast.error(detail);
    }
    setScanning(false);
  };

  const confirmReceipt = async () => {
    if (!scanResult || scanResult.items.length === 0) return;
    setConfirming(true);
    try {
      await api.post("/api/budget/confirm-receipt", {
        vendor: scanResult.vendor,
        date: scanResult.date,
        items: scanResult.items.map((i) => ({
          type: i.type, amount: Number(i.amount) || 0,
          category: i.category, description: i.description, date: scanResult.date,
        })),
      });
      toast.success(`Added ${scanResult.items.length} entries`);
      setScanResult(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to save");
    }
    setConfirming(false);
  };

  const updateScanItem = (idx, field, value) => {
    setScanResult((s) => ({
      ...s,
      items: s.items.map((it, i) => i === idx ? { ...it, [field]: value } : it),
    }));
  };
  const removeScanItem = (idx) => {
    setScanResult((s) => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));
  };

  const { income, expenses, balance, byCategory } = useMemo(() => {
    const month = new Date().toISOString().slice(0, 7);
    const monthEntries = entries.filter((e) => e.date?.startsWith(month));
    const income = monthEntries.filter((e) => e.type === "income").reduce((a, b) => a + b.amount, 0);
    const expenses = monthEntries.filter((e) => e.type === "expense").reduce((a, b) => a + b.amount, 0);
    const byCategory = {};
    monthEntries.filter((e) => e.type === "expense").forEach((e) => {
      byCategory[e.category] = (byCategory[e.category] || 0) + e.amount;
    });
    return { income, expenses, balance: income - expenses, byCategory };
  }, [entries]);

  const maxCat = Math.max(...Object.values(byCategory), 1);

  return (
    <div data-testid="budget-page">
      <PageHeader
        title="Family Budget"
        subtitle="Track income & expenses for the month."
        color="#E8B96B"
        action={
          <div className="flex gap-2 flex-wrap">
            <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp" hidden onChange={onReceiptPicked} data-testid="receipt-input" />
            <NBButton testId="scan-receipt-btn" color="#E9B5A0" onClick={() => fileInputRef.current?.click()} disabled={scanning}>
              {scanning ? (
                <><Sparkles strokeWidth={2.4} className="w-4 h-4 inline mr-1 animate-pulse" />Scanning…</>
              ) : (
                <><ScanLine strokeWidth={2.4} className="w-4 h-4 inline mr-1" />Scan receipt</>
              )}
            </NBButton>
            <NBButton testId="add-budget-btn" onClick={() => setOpen(true)}><Plus strokeWidth={2.4} className="w-4 h-4 inline mr-1" />Entry</NBButton>
          </div>
        }
      />

      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        <NBCard color="#B8CFA9">
          <TrendingUp strokeWidth={2.5} className="mb-2" />
          <p className="text-xs font-black uppercase tracking-wider">Income</p>
          <p className="text-3xl font-black" style={{ fontFamily: "var(--hh-display)" }} data-testid="budget-income">${income.toFixed(2)}</p>
        </NBCard>
        <NBCard color="#E9B5A0">
          <TrendingDown strokeWidth={2.5} className="mb-2" />
          <p className="text-xs font-black uppercase tracking-wider">Expenses</p>
          <p className="text-3xl font-black" style={{ fontFamily: "var(--hh-display)" }} data-testid="budget-expenses">${expenses.toFixed(2)}</p>
        </NBCard>
        <NBCard color={balance >= 0 ? "#E8B96B" : "#E9B5A0"}>
          <DollarSign strokeWidth={2.5} className="mb-2" />
          <p className="text-xs font-black uppercase tracking-wider">Balance</p>
          <p className="text-3xl font-black" style={{ fontFamily: "var(--hh-display)" }} data-testid="budget-balance">${balance.toFixed(2)}</p>
        </NBCard>
      </div>

      {Object.keys(byCategory).length > 0 && (
        <NBCard className="mb-6" hover={false}>
          <h3 className="text-xl font-black mb-4" style={{ fontFamily: "var(--hh-display)" }}>By Category</h3>
          <div className="space-y-2">
            {Object.entries(byCategory).map(([cat, amt]) => (
              <div key={cat}>
                <div className="flex justify-between text-sm font-bold mb-1">
                  <span className="capitalize">{cat}</span>
                  <span>${amt.toFixed(2)}</span>
                </div>
                <div className="h-3 bg-[#F6EFE4] border-2 border-black rounded-full overflow-hidden">
                  <div className="h-full bg-[#E9B5A0] border-r-2 border-black" style={{ width: `${(amt / maxCat) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </NBCard>
      )}

      <h3 className="text-2xl font-black mb-4" style={{ fontFamily: "var(--hh-display)" }}>Recent</h3>
      {entries.length === 0 ? (
        <EmptyState title="No entries yet" subtitle="Track income & expenses here." />
      ) : (
        <div className="space-y-2">
          {entries.slice(0, 30).map((e) => (
            <div key={e.id} className="nb-card !p-3 flex items-center gap-3" data-testid={`budget-entry-${e.id}`}>
              <div className={`w-10 h-10 rounded-lg border-2 border-black flex items-center justify-center shrink-0 ${e.type === "income" ? "bg-[#B8CFA9]" : "bg-[#E9B5A0]"}`}>
                {e.type === "income" ? <TrendingUp strokeWidth={2.5} className="w-5 h-5" /> : <TrendingDown strokeWidth={2.5} className="w-5 h-5" />}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold capitalize">{e.category}</p>
                <p className="text-xs text-neutral-600">{e.description || e.date}</p>
              </div>
              <p className={`font-black ${e.type === "income" ? "text-emerald-700" : "text-rose-700"}`} style={{ fontFamily: "var(--hh-display)" }}>
                {e.type === "income" ? "+" : "-"}${e.amount.toFixed(2)}
              </p>
              <button onClick={() => remove(e.id)} className="nb-btn bg-[#E9B5A0] p-1.5">
                <Trash2 strokeWidth={2.5} className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      {open && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setOpen(false)}>
          <div className="nb-card max-w-md w-full p-6" onClick={(e) => e.stopPropagation()} style={{ boxShadow: "8px 8px 0 0 rgba(0,0,0,1)" }}>
            <div className="flex justify-between mb-4">
              <h2 className="text-2xl font-black" style={{ fontFamily: "var(--hh-display)" }}>New Entry</h2>
              <button onClick={() => setOpen(false)} className="nb-btn bg-[#E9B5A0] p-1.5"><X strokeWidth={3} className="w-4 h-4" /></button>
            </div>
            <form onSubmit={submit} className="space-y-3">
              <div className="flex gap-2">
                {["expense", "income"].map((t) => (
                  <button type="button" key={t} onClick={() => setForm({ ...form, type: t })} data-testid={`budget-type-${t}`}
                    className={`nb-btn flex-1 px-4 py-2 text-sm capitalize ${form.type === t ? (t === "income" ? "bg-[#B8CFA9]" : "bg-[#E9B5A0]") : "bg-white"}`}>
                    {t}
                  </button>
                ))}
              </div>
              <NBInput testId="budget-amount" required type="number" step="0.01" min="0" placeholder="Amount" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} />
              <NBSelect testId="budget-category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </NBSelect>
              <NBInput testId="budget-desc" placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              <NBInput testId="budget-date" required type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} />
              <NBButton testId="submit-budget" type="submit" className="w-full">Save</NBButton>
            </form>
          </div>
        </div>
      )}

      {scanResult && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" data-testid="scan-preview-modal">
          <div className="nb-card max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-start justify-between p-6 pb-4" style={{ borderBottom: "1px solid var(--hh-line)" }}>
              <div>
                <div className="flex items-center gap-2 mb-1.5">
                  <ScanLine strokeWidth={2.2} className="w-4 h-4" style={{ color: "var(--hh-terracotta)" }} />
                  <span className="text-[10px] uppercase tracking-widest" style={{ color: "var(--hh-ink-soft)" }}>Receipt scan · {scanResult.confidence} confidence</span>
                </div>
                <h2 className="text-2xl font-extrabold leading-tight" style={{ fontFamily: "var(--hh-display)" }}>
                  {scanResult.vendor || "Receipt"}
                </h2>
                {scanResult.total != null && (
                  <p className="text-sm mt-1" style={{ color: "var(--hh-ink-soft)" }}>
                    Total on receipt: <strong>{scanResult.currency} {Number(scanResult.total).toFixed(2)}</strong>
                  </p>
                )}
              </div>
              <button onClick={() => setScanResult(null)} className="nb-btn p-1.5" data-testid="close-scan-preview">
                <X strokeWidth={2.4} className="w-4 h-4" />
              </button>
            </div>

            <div className="p-6">
              <div className="grid grid-cols-2 gap-3 mb-5">
                <div>
                  <label className="text-[10px] uppercase tracking-widest mb-1.5 block" style={{ color: "var(--hh-ink-soft)" }}>Vendor</label>
                  <NBInput testId="scan-vendor" value={scanResult.vendor} onChange={(e) => setScanResult({ ...scanResult, vendor: e.target.value })} placeholder="Vendor (optional)" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest mb-1.5 block" style={{ color: "var(--hh-ink-soft)" }}>Date</label>
                  <NBInput testId="scan-date" type="date" value={scanResult.date} onChange={(e) => setScanResult({ ...scanResult, date: e.target.value })} />
                </div>
              </div>

              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: "var(--hh-ink-soft)" }}>Line items · review &amp; edit</h3>
                <span className="text-xs" style={{ color: "var(--hh-ink-soft)" }}>{scanResult.items.length}</span>
              </div>

              {scanResult.items.length === 0 ? (
                <p className="text-sm py-6 text-center" style={{ color: "var(--hh-ink-soft)" }}>
                  No items detected. Try a clearer photo or add entries manually.
                </p>
              ) : (
                <div className="space-y-2 mb-5">
                  {scanResult.items.map((it, idx) => (
                    <div key={it._id} className="grid gap-2" style={{ gridTemplateColumns: "1fr 110px 130px 36px", alignItems: "center" }} data-testid={`scan-item-${idx}`}>
                      <NBInput value={it.description} onChange={(e) => updateScanItem(idx, "description", e.target.value)} placeholder="Description" />
                      <NBInput type="number" step="0.01" min="0" value={it.amount} onChange={(e) => updateScanItem(idx, "amount", e.target.value)} />
                      <NBSelect value={it.category} onChange={(e) => updateScanItem(idx, "category", e.target.value)}>
                        {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                      </NBSelect>
                      <button onClick={() => removeScanItem(idx)} className="nb-btn p-1.5" data-testid={`remove-scan-item-${idx}`} title="Remove">
                        <Trash2 strokeWidth={2.4} className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div className="flex gap-2 justify-end pt-4" style={{ borderTop: "1px solid var(--hh-line)" }}>
                <NBButton onClick={() => setScanResult(null)}>Discard</NBButton>
                <NBButton testId="confirm-scan-btn" color="#B8CFA9" onClick={confirmReceipt} disabled={confirming || scanResult.items.length === 0}>
                  <Camera strokeWidth={2.4} className="w-4 h-4 inline mr-1" />
                  {confirming ? "Saving…" : `Save ${scanResult.items.length} entries`}
                </NBButton>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
